/**
 * VoiceShiftHandoffModal — caregiver records 30-90s about the day; AI
 * summarises into a tight handoff packet (key_points, concerns, todo, mood)
 * for the next caregiver to read at the start of their shift.
 *
 *   <VoiceShiftHandoffModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     clientId={clientId}
 *     onSaved={(handoff) => toast.success("Handoff sent")}
 *   />
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Mic, Square, Loader2, Sparkles, X, Volume2, AlertTriangle, Check } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";
import api from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  "Margaret had a calm day, ate well at lunch. Her hip seems sore on the left — watch when she stands. Give 2pm meds with applesauce.",
  "Tough morning, Henry was anxious. Played his classical music and that calmed him down. Make sure to do that again tomorrow if he gets agitated.",
  "Great shift. No concerns. She finished the crossword and was in a wonderful mood.",
];

export default function VoiceShiftHandoffModal({ open, onClose, clientId, onSaved }) {
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const rec = useVoiceRecorder({
    endpoint: `/clients/${clientId}/handoffs`,
    filenamePrefix: "handoff",
    limitMs: 120 * 1000,
    onResult: (data) => setResult(data),
  });

  const close = () => {
    rec.reset();
    setResult(null);
    setSaving(false);
    onClose?.();
  };

  const send = async () => {
    if (!result) return;
    setSaving(true);
    try {
      // The voice endpoint already PERSISTED the handoff on the server.
      // We just acknowledge & toast on the client side.
      toast.success("Handoff sent to next caregiver");
      onSaved?.(result);
      close();
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (!result?.id) {
      close();
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/handoffs/${result.id}`);
      toast.success("Handoff discarded");
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not discard");
      setSaving(false);
    }
  };

  if (!open) return null;

  const mood = result?.mood;
  const MOOD_COLOR = {
    great: "bg-emerald-100 text-emerald-800",
    okay: "bg-amber-100 text-amber-900",
    rough: "bg-rose-100 text-rose-800",
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-16 overflow-y-auto"
      data-testid="voice-handoff-modal"
    >
      <div className="card-surface w-full max-w-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#0891b21a", color: "#0891b2" }}
            >
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice handoff for next caregiver</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Tell the next caregiver what they need to know."}
                {rec.isRecording && `Listening · ${rec.mmss}`}
                {rec.isProcessing && "Summarising…"}
                {result && "Review and send"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close"
            data-testid="voice-handoff-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-3 py-4">
              {rec.isIdle && (
                <div className="text-center space-y-2">
                  <Volume2 className="mx-auto h-10 w-10 text-clinical-subtext/50" />
                  <p className="text-xs text-clinical-subtext">2-min limit.</p>
                  <div className="mt-3 rounded-xl border border-cyan-200 bg-cyan-50/60 p-3 text-left"
                       data-testid="voice-handoff-examples">
                    <div className="label-eyebrow text-cyan-700 mb-1.5">Try saying</div>
                    <ul className="space-y-1.5 text-xs text-cyan-900">
                      {EXAMPLES.map((ex, i) => (
                        <li key={i} className="italic">"{ex}"</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-cyan-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-cyan-600 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text"
                       data-testid="voice-handoff-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-cyan-600 text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-handoff-stop" : "voice-handoff-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop" : rec.isProcessing ? "Processing…" : "Start recording"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-handoff-results">
              {result.transcript && (
                <div className="rounded-lg bg-clinical-bg/60 p-3">
                  <div className="label-eyebrow mb-1">You said</div>
                  <p className="text-sm text-clinical-text">"{result.transcript}"</p>
                </div>
              )}
              {result.summary && (
                <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="label-eyebrow text-cyan-700">Summary</div>
                    {mood && (
                      <span className={`pill ${MOOD_COLOR[mood] || "bg-clinical-bg"}`}
                            data-testid="voice-handoff-mood">{mood}</span>
                    )}
                  </div>
                  <p className="text-sm text-clinical-text">{result.summary}</p>
                </div>
              )}
              {(result.key_points || []).length > 0 && (
                <div>
                  <div className="label-eyebrow">Key points</div>
                  <ul className="list-disc pl-5 text-sm text-clinical-text">
                    {result.key_points.map((p, i) => (<li key={i}>{p}</li>))}
                  </ul>
                </div>
              )}
              {(result.concerns || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                  <div className="label-eyebrow text-amber-800 mb-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> Concerns
                  </div>
                  <ul className="list-disc pl-5 text-sm text-amber-900">
                    {result.concerns.map((c, i) => (<li key={i}>{c}</li>))}
                  </ul>
                </div>
              )}
              {(result.todo || []).length > 0 && (
                <div>
                  <div className="label-eyebrow">Next caregiver to do</div>
                  <ul className="list-disc pl-5 text-sm text-clinical-text">
                    {result.todo.map((t, i) => (<li key={i}>{t}</li>))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {result && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button
              type="button"
              onClick={discard}
              disabled={saving}
              className="text-sm text-rose-600 hover:underline disabled:opacity-50"
              data-testid="voice-handoff-discard"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={send}
              disabled={saving}
              className="btn-primary disabled:opacity-50"
              data-testid="voice-handoff-send"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Send to next caregiver
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
